import React from 'react';
import {observer} from 'mobx-react';
import {observable} from 'mobx';
import {FormattedMessage} from 'react-intl';
import _ from 'lodash';
import {t, withState} from 'shared/core';
import {
  Display, Layout, Panel, FormLayout, Input, TextArea, DatePicker,
  NumberInput, Select2, Button, UploadFiles, ActionLink, LabelHelper
} from 'shared/components';
import ActionLinks from 'main/components/tables/components/ActionLinks';
import {endpoints} from 'main/tools';
import {EditExpenseState} from '../state';

const ExpenseFields = observer(({uiState, expense, errors}) => {
  return (
    <div className='p2 mb2 bg-white border rounded relative'>
      <FormLayout>
        <FormLayout.Group>
          <Input
            label='expenses.edit.Title'
            value={expense.title}
            onChange={e => expense.title = e.target.value}
            errorMessage={errors.title}
          />
          <Select2
            label='expenses.edit.Category'
            by='name'
            defaultValue={expense.nmbrPresetId}
            endpoint={endpoints.NMBR.PRESETS.ALL}
            onChange={e => uiState.updateCategory(expense, e.target.value, e.target.options[e.target.selectedIndex].text)}
            errorMessage={errors.nmbrPresetId}
          >
            <option value={expense.nmbrPresetId}>{expense.category}</option>
          </Select2>
        </FormLayout.Group>
        <FormLayout.Group>
          <DatePicker
            label='expenses.edit.Date'
            value={expense.date}
            onChange={date => expense.date = date}
            errorMessage={errors.date}
          />
          <NumberInput
            label='expenses.edit.Total'
            min={0}
            value={expense.amount}
            onChange={e => expense.amount = e.target.value}
            errorMessage={errors.amount}
            connectedLeft={
              <div className='InputGroup'>
                <span className='InputGroup-tag small'>{'$'}</span>
              </div>
            }
          />
        </FormLayout.Group>
        <Input
          label='expenses.edit.Note'
          value={expense.note}
          onChange={e => expense.note = e.target.value}
          errorMessage={errors.note}
        />
        <div>
          <LabelHelper label={t('expenses.edit.Receipts')}/>
          <UploadFiles
            defaultFiles={expense.receipts}
            onChange={files => expense.receipts.replace(files)}
            modelType='expenses/receipt'
          />
        </div>
      </FormLayout>
      <div className='absolute top-0 right-0 p1'>
        <ActionLinks
          model={expense}
          onlyShowCustomLinks
          customLinks={[{
            order: 0,
            text: 'expenses.edit.Remove',
            action: () => uiState.removeExpense(expense)
          }]}
        />
      </div>
    </div>
  );
});

const EditExpenseContainer = observer(({uiState}) => {
  const {request, errors, total, isEditing, backPath, backTitle} = uiState;

  return (
    <div>
      <Display
        title={isEditing ? t('expenses.edit.Edit expense request') : t('expenses.edit.New expense request')}
        backPath={backPath}
        backTitle={backTitle}
      />
      <Layout>
        <Panel.Stack loose>
          <Panel>
            <Panel.Header
              title={t('expenses.edit.Request details')}
              description={t('expenses.edit.Provide a title and brief description for this expense request.')}
            />
            <FormLayout>
              <Input
                label='expenses.edit.Title'
                value={request.title}
                onChange={e => request.title = e.target.value}
                errorMessage={errors.title}
              />
              <TextArea
                label='expenses.edit.Description'
                value={request.description}
                minRows={3}
                onChange={e => request.description = e.target.value}
                errorMessage={errors.description}
              />
            </FormLayout>
          </Panel>
          <Panel>
            <Panel.Header
              title={t('expenses.edit.Expenses')}
              description={t('expenses.edit.Add each individual expense with a category and receipt.')}
            />
            {request.expenses.map((expense, index) => (
              <ExpenseFields
                key={index}
                uiState={uiState}
                expense={expense}
                errors={_.get(errors, `expenses[${index}]`, {})}
              />
            ))}
            <ActionLink className='TextLink dodger inline-block mb2' onClick={() => uiState.addExpense()}>
              <FormattedMessage id='expenses.edit.+ Add expense'/>
            </ActionLink>
            <Panel.Separator/>
            <div className='flex justify-content-between align-items-center mt2'>
              <div className='h2 medium'>
                <FormattedMessage id='expenses.edit.TOTAL' values={{total}}/>
              </div>
              <Button onClick={() => uiState.submit()}>
                <FormattedMessage id={isEditing ? 'expenses.edit.Save' : 'expenses.edit.Submit request'}/>
              </Button>
            </div>
          </Panel>
        </Panel.Stack>
      </Layout>
    </div>
  );
});

export default withState(EditExpenseContainer, EditExpenseState);
